import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './auth';
import {
  SUPPORTED_CURRENCIES,
  defaultCurrencyForLanguage,
  formatCurrency as formatCurrencyUtil,
  getCurrencyParts,
} from '../Utils/locale';

const STORAGE_KEY = 'currency';
const SUPPORTED_CODES = SUPPORTED_CURRENCIES.map((c) => c.code);

const CurrencyContext = createContext(null);

function readStoredCurrency() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_CODES.includes(stored)) return stored;
  } catch (err) {}
  return null;
}

function writeStoredCurrency(value) {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, value);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {}
}

export function CurrencyProvider({ children }) {
  const { i18n } = useTranslation();
  const language = i18n.language || 'pt';
  const { user } = useContext(AuthContext);

  const [currency, setCurrencyState] = useState(
    () => readStoredCurrency() || defaultCurrencyForLanguage(language)
  );

  useEffect(() => {
    if (user && user.currency && SUPPORTED_CODES.includes(user.currency)) {
      setCurrencyState(user.currency);
      writeStoredCurrency(user.currency);
    } else if (user === null) {
      writeStoredCurrency(null);
      setCurrencyState(defaultCurrencyForLanguage(language));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, language]);

  const setCurrency = useCallback((next) => {
    if (!SUPPORTED_CODES.includes(next)) return;
    setCurrencyState(next);
    writeStoredCurrency(next);
  }, []);

  const value = useMemo(() => {
    const parts = getCurrencyParts(currency);
    return {
      currency,
      setCurrency,
      symbol: parts.symbol,
      parts,
      format: (amount) => formatCurrencyUtil(amount, currency),
      currencies: SUPPORTED_CURRENCIES,
    };
  }, [currency, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency precisa estar dentro de um CurrencyProvider');
  }
  return ctx;
}

export default CurrencyProvider;
